import { RegisterOptions } from "react-hook-form";
import { InputActionMeta } from "react-select";

import {
  ButtonProps,
  FieldsetProps,
  IImagePreviewPreset,
} from "shared/components";
import { ToolbarItems } from "shared/components/Form/Fields/RTE/components";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { DateFormat, TimeFormat, Unit } from "shared/utils";

export interface ICheckboxGroup {
  type: "checkbox";
  name: string;
  label: string;
  note?: string;
  required?: boolean;
  options: IFormOption[];
}

export interface IFormOption {
  label: string | JSX.Element;
  value: string;
  icon?: React.ComponentType<any>;
  disabled?: boolean;
  onClick?: () => void;
  options?: IFormOption[];
  defaultChecked?: boolean;
}

export interface IFormButton extends ButtonProps {
  children?: string | JSX.Element;
  isSubmitButton?: boolean;
}

export type IFormFields = (
  | IFormField
  | IFormFieldset
  | IFormWidget
  | IFormComponent
)[];

export interface IFormSchema {
  fields: IFormFields;
  defaultValues?: Record<string, any>;
  buttons?: IFormButtons;
}

export interface IFormButtons {
  primary?: boolean | IFormButton;
  secondary?: boolean | IFormButton;
  tabIndex?: number;
}

export interface IFieldLink {
  label: string;
  hideLabel?: boolean;
  onClick: () => void;
  "data-testid"?: string;
}

export type IFormFieldStyle = React.CSSProperties & {
  mobile?: React.CSSProperties;
};

/**
 * @param fieldset - Fieldset name, string used to  group elements deeper objects
 */
export interface IBaseFormField {
  name: string;
  path?: string[];
  label: string | JSX.Element; // required for a11y
  hideLabel?: boolean; // if you don't want to show the label visually
  placeholder?: string;
  // type: IFieldType;
  suffix?: string | JSX.Element;
  note?: string | JSX.Element;
  disabled?: boolean;
  readOnly?: boolean;
  isHidden?: boolean;
  autocomplete?: string;
  fieldLink?: IFieldLink;
  style?: IFormFieldStyle;
  validation?: RegisterOptions;
  autoFocus?: boolean;
  tabIndex?: number;
  onChange?: (data: any, prevData?: any) => void;
}

export interface ITextAreaField extends IBaseFormField {
  type: IFieldType.textarea;
  style?: IFormFieldStyle & {
    rows?: number;
    cols?: number;
  };
}

export interface INumberFieldProps {
  min?: number;
  max?: number;
  decimalScale?: number;
  allowNegative?: boolean;
}

export interface INumberField extends IBaseFormField, INumberFieldProps {
  type: IFieldType.number;
}

export interface IDimensionField extends IBaseFormField, INumberFieldProps {
  type: IFieldType.dimension;
  isFullWidth?: boolean;
  unit?: {
    metric: Unit;
    imperial: Unit;
  };
}

export interface ISelectField extends IBaseFormField {
  type: IFieldType.select;
  options: ISelectOption[];
  isSearchable?: boolean;
  isLoading?: boolean;
  isCreatable?: boolean;
  isMulti?: boolean;
  onDropdownClose?: () => void;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
}

export interface IAsyncSelectField extends Omit<ISelectField, "type"> {
  type: IFieldType.asyncSelect;
  loadOptions?: (query: string) => Promise<ISelectOption[]>;
}

export interface IPeoplePickerSettings {
  excludeOptions?: string[];
  hideCategories?: boolean;
  hideNickname?: boolean;
  includeUsersFromAllBands?: boolean;
  includeInvitedUsers?: boolean;
  filterOnStatus?: boolean;
}

export interface ISinglePeoplePickerField extends IBaseFormField {
  type: IFieldType.singlePeoplePicker;
  isSearchable?: boolean;
  settings?: IPeoplePickerSettings;
}

export interface IPeoplePickerField extends IBaseFormField {
  type: IFieldType.peoplePicker;
  settings?: IPeoplePickerSettings;
}

export interface IWebsiteField extends IBaseFormField {
  type: IFieldType.website;
  hideErrorMessage?: boolean;
}

export const IFileType = {
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  heif: "image/heif",
  pdf: "application/pdf",
  zip: "application/zip",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  doc: "application/msword",
  rtf: "application/rtf",
  txt: "text/plain",
  csv: "text/csv",
  xls: "application/vnd.ms-excel",
  pps: "application/vnd.ms-powerpoint",
  odp: "application/vnd.oasis.opendocument.presentation",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  odt: "application/vnd.oasis.opendocument.text",
  eps: "application/postscript",
  gif: "image/gif",
  jpg: "image/jpeg",
  tif: "image/tiff",
  tiff: "image/tiff",
  ai: "application/illustrator",
  indd: "application/x-indesign",
  psd: "image/vnd.adobe.photoshop",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  aac: "audio/aac",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  xml: "application/xml",
  mpp: "application/vnd.ms-project",
  dwg: "image/vnd.dwg",
  dxf: "image/vnd.dxf",
  mvr: "application/x-mvr",
  wyg: "application/x-wyg",
  wyt: "application/x-wyt",
  d4: "application/x-d4",
  afl: "application/x-afl",
  cbs: "application/x-cbs",
  cf: "application/x-cf",
  gma: "application/x-gma",
  gll: "application/x-gll",
  sma: "application/x-sma",
  stn: "application/x-stn",
  lake: "application/x-lake",
  lcs: "application/x-lcs",
  dbac: "application/x-dbac",
  dbpr: "application/x-dbpr",
  svl: "application/x-svl",
  mapp: "application/x-mapp",
  ns1: "application/x-ns1",
  qsys: "application/x-qsys",
  yse: "application/x-yse",
  show: "application/x-show",
  scp: "application/x-scp",
  ptx: "application/x-ptx",
  pts: "application/x-pts",
  ven: "application/x-ven",
  sd7: "application/x-sd7",
  sd9: "application/x-sd9",
  sd11: "application/x-sd11",
  sslshow: "application/x-sslshow",
  midas: "application/x-midas",
  "7z": "application/x-7z-compressed",
  rar: "application/x-rar-compressed",
  svg: "image/svg+xml",
  webp: "image/webp",
};

export const IImageTypes = [
  IFileType.jpg,
  IFileType.jpeg,
  IFileType.png,
  IFileType.heic,
  IFileType.heif,
  IFileType.webp,
];

export interface IFileValidation extends RegisterOptions {
  type?: {
    types: any;
    message?: string;
  };
  fileSize?: {
    maxSize: number;
    message?: string;
  };
}

export interface IFileField extends IBaseFormField {
  type: IFieldType.file | IFieldType.image | IFieldType.files;
  validation?: IFileValidation;
  handleFileChange?: (file: File) => void;
}

export interface IImageField extends IFileField {
  type: IFieldType.image;
  preset?: IImagePreviewPreset;
}

export interface IPasswordValidation extends RegisterOptions {
  match?: {
    name: string;
    message: string;
  };
}

export interface IPasswordField extends IBaseFormField {
  type: IFieldType.password;
  validation?: IPasswordValidation;
}

export interface ICheckboxGroupField extends IBaseFormField {
  type: IFieldType.checkboxGroup;
  options: IFormOption[];
  subText?: string;
}

export interface IDraggableCheckboxGroup extends IBaseFormField {
  type: IFieldType.draggableCheckboxGroup;
  subText?: string;
  options: IFormOption[];
  setOptions: (options: IFormOption[]) => void;
}

export interface IRadioGroupField extends IBaseFormField {
  type: IFieldType.radioGroup;
  options: IFormOption[];
  isInline?: boolean;
}

export interface ISearchField extends IBaseFormField {
  type: IFieldType.search;
  hasButton?: boolean;
}

export interface IRTEField extends IBaseFormField {
  type: IFieldType.rte;
  excludeItems?: { [key in ToolbarItems]?: boolean };
  size?: "s" | "m";
}

export interface ITagGroup extends IBaseFormField {
  type: IFieldType.tagGroup;
  isMultiple?: boolean;
  options: IFormOption[];
  showViewLabel?: boolean;
}

export interface ISimpleTimeField extends IBaseFormField {
  type: IFieldType.simpleTime;
  timeFormat?: TimeFormat;
}

export interface ISimpleDateField extends IBaseFormField {
  type: IFieldType.simpleDate;
  dateFormat?: DateFormat;
}

export interface IDatePickerField extends IBaseFormField {
  type: IFieldType.datePicker;
  dateFormat?: DateFormat;
  minDate?: Date;
}

export interface IOtherTextField extends IBaseFormField {
  type: IFieldType.otherText;
  button?: string;
}

export interface ICheckboxField extends IBaseFormField {
  type: IFieldType.checkbox;
  superLabel?: string | JSX.Element;
}

export interface IMultiCreatableSelect extends Omit<ISelectField, "type"> {
  type: IFieldType.multiCreatableSelect;
}

export interface IBasicFormField extends IBaseFormField {
  type:
    | IFieldType.text
    | IFieldType.email
    | IFieldType.checkbox
    | IFieldType.country
    | IFieldType.phone
    | IFieldType.color
    | IFieldType.timezone
    | IFieldType.peoplePicker
    | IFieldType.singlePeoplePicker
    | IFieldType.vat;
}

export type IFormField =
  | ITextAreaField
  | INumberField
  | ISelectField
  | IAsyncSelectField
  | IDimensionField
  | IPasswordField
  | IFileField
  | IImageField
  | ICheckboxGroupField
  | IRadioGroupField
  | IBasicFormField
  | IWebsiteField
  | ISearchField
  | IRTEField
  | ITagGroup
  | ISimpleTimeField
  | ISimpleDateField
  | IDatePickerField
  | ISinglePeoplePickerField
  | IPeoplePickerField
  | IOtherTextField
  | IDraggableCheckboxGroup
  | ICheckboxField
  | IMultiCreatableSelect;

/**
 * @param name - String used to  group elements deeper objects
 */
export interface IFormFieldset extends FieldsetProps {
  name?: string;
  path?: string[];
  fields: (IFormField | IFormFieldset | IFormWidget)[];
}

export enum IFormWidgetType {
  "unitConverter",
  "title",
  "note",
}

export interface IFormWidget {
  path?: string[];
  name: string;
  label?: string;
  widget: IFormWidgetType;
  props?: any;
  style?: IFormFieldStyle;
}

export interface IFormComponent {
  name: string;
  component: JSX.Element;
  style?: IFormFieldStyle;
}

export enum IFieldType {
  text = "text",
  email = "email",
  password = "password",
  website = "website",
  checkbox = "checkbox",
  checkboxGroup = "checkboxGroup",
  radioGroup = "radioGroup",
  country = "country",
  phone = "phone",
  number = "number",
  dimension = "dimension",
  textarea = "textarea",
  select = "select",
  asyncSelect = "asyncSelect",
  file = "file",
  files = "files",
  image = "image",
  search = "search",
  color = "color",
  rte = "rte",
  simpleDate = "simpleDate",
  simpleTime = "simpleTime",
  datePicker = "datePicker",
  tagGroup = "tagGroup",
  timezone = "timezone",
  peoplePicker = "peoplePicker",
  singlePeoplePicker = "singlePeoplePicker",
  vat = "vat",
  otherText = "otherText",
  draggableCheckboxGroup = "draggableCheckboxGroup",
  multiCreatableSelect = "multiCreatableSelect",
}

export enum IAutocomplete {
  off = "off",
  email = "email",
  givenName = "given-name",
  familyName = "family-name",
  newPassword = "new-password",
  currentPassword = "current-password",
  organizationTitle = "organization-title", // eg. manager, assistant
  organization = "organization",
  addressLine1 = "address-line1",
  addressLine2 = "address-line2",
  country = "country", // code
  countryName = "country-name",
  postalCode = "postal-code",
  ccName = "cc-name", // cc = credit card
  ccNumber = "cc-number",
  ccExpiry = "cc-exp", // "MM/YY" or "MM/YYYY"
  ccSecurityCode = "cc-csc", // 3 digit verification
  ccType = "cc-type", // visa, mastercard...
  transactionCurrency = "transaction-currency",
  transactionAmount = "transaction-amount",
  bday = "bday", // full date
  tel = "tel", // full number
  telCountryCode = "tel-country-code",
  telNational = "tel-national", // tel withour country code
  url = "url",
}
